import { useState } from 'react';
import { AlertTriangle, Shield, ShieldOff } from 'lucide-react';
import api from '../../services/api';

interface ACLDisableButtonProps {
  edgeGuid: string;
  edgeName?: string;
  isEnabled: boolean;
  onStatusChange?: (enabled: boolean) => void;
}

const ACLDisableButton = ({ edgeGuid, edgeName, isEnabled, onStatusChange }: ACLDisableButtonProps) => {
  const [loading, setLoading] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleToggle = async () => {
    if (isEnabled) {
      setShowConfirm(true);
    } else {
      await enableACL();
    }
  };

  const disableACL = async () => {
    try {
      setLoading(true);
      // This would delete all ACLs or mark them as disabled
      // For now, we just show the intent - actual implementation depends on backend
      await api.delete(`/api/v2/edges/${edgeGuid}/acl`);
      onStatusChange?.(false);
      setShowConfirm(false);
    } catch (error) {
      console.error('Failed to disable ACL:', error);
    } finally {
      setLoading(false);
    }
  };

  const enableACL = async () => {
    try {
      setLoading(true);
      // Re-enable ACL - this would trigger a config sync
      // Actual implementation depends on backend
      onStatusChange?.(true);
    } catch (error) {
      console.error('Failed to enable ACL:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <button
        onClick={handleToggle}
        disabled={loading}
        className={`inline-flex items-center px-3 py-2 rounded-md text-sm font-medium transition-colors ${
          isEnabled
            ? 'bg-red-100 text-red-700 hover:bg-red-200 border border-red-300'
            : 'bg-green-100 text-green-700 hover:bg-green-200 border border-green-300'
        } disabled:opacity-50`}
      >
        {isEnabled ? (
          <>
            <ShieldOff className="w-4 h-4 mr-2" />
            Disable ACL
          </>
        ) : (
          <>
            <Shield className="w-4 h-4 mr-2" />
            Enable ACL
          </>
        )}
      </button>

      {/* Confirmation Modal */}
      {showConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
            <div className="flex items-start mb-4">
              <div className="flex-shrink-0 p-2 bg-red-100 rounded-full">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div className="ml-4">
                <h3 className="text-lg font-semibold text-gray-900">
                  Disable ACL Protection?
                </h3>
                <p className="mt-2 text-sm text-gray-600">
                  This will disable all Access Control Lists on{' '}
                  <span className="font-medium">{edgeName || edgeGuid}</span>.
                </p>
                <p className="mt-2 text-sm text-red-600 font-medium">
                  Warning: All traffic filtering rules will be removed. This may expose the network to security risks.
                </p>
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                onClick={() => setShowConfirm(false)}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-md hover:bg-gray-200"
              >
                Cancel
              </button>
              <button
                onClick={disableACL}
                disabled={loading}
                className="px-4 py-2 text-sm font-medium text-white bg-red-600 rounded-md hover:bg-red-700 disabled:opacity-50"
              >
                {loading ? 'Disabling...' : 'Disable ACL'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ACLDisableButton;
